import { type FC, type KeyboardEvent, type ReactNode, useCallback } from "react";

import { type Status } from "@snapchat/snap-design-system";

import { SurfaceType } from "../../gql-generated/types";
import { BadgeAnimated } from "../../shared/components";
import { PreviewAspectRatio, Scale } from "../models";

import { LensPreviewElement, LensPreviewTextContainer } from "./LensPreview.styles";
import { ImagePreview, PreviewViewer, grayBackground, reviewedNotification } from "./LensPreviewViewer.styles";
import { VideoViewer } from "./VideoViewer";
import { LensPreviewAddCta, PreviewAdd, PreviewSnapchat, PreviewSpectacles } from "./messages.generated";

interface PreviewViewerWithCTAProps {
    isVideo: boolean;
    src?: string;
    badgeText?: ReactNode;
    badgeStatus?: Status;
    scale?: Scale;
    aspectRatio?: PreviewAspectRatio;
    autoplay?: boolean;
    isEmpty?: boolean;
    surfaceType?: SurfaceType;
    isReadOnly?: boolean;
    disabled?: boolean;
    showModal?: () => void;
}

export const PreviewViewerWithCTA: FC<PreviewViewerWithCTAProps> = ({
    scale,
    aspectRatio = PreviewAspectRatio.nineSixteen,
    badgeText,
    badgeStatus,
    src,
    isVideo,
    autoplay,
    isEmpty = false,
    surfaceType,
    isReadOnly,
    disabled,
    showModal,
}) => {
    const onKeypressShowModalTrigger = useCallback(
        ({ nativeEvent }: KeyboardEvent<HTMLDivElement>) => {
            if (nativeEvent.key !== "Enter") {
                return;
            }
            showModal?.();
        },
        [showModal],
    );

    const renderPreviewContent = useCallback(() => {
        if ((!src || isEmpty) && scale === Scale.Percent100) {
            return (
                <LensPreviewElement
                    role={isReadOnly ? "" : "button"}
                    tabIndex={0}
                    onClick={!!disabled || isReadOnly ? undefined : showModal}
                    onKeyPress={onKeypressShowModalTrigger}
                    scale={scale}
                    cursor={isReadOnly ? "default" : "pointer"}
                    aspectRatio={aspectRatio}
                    disabled={!!disabled || isReadOnly}
                >
                    <LensPreviewTextContainer>
                        {!isReadOnly && (
                            <LensPreviewAddCta
                                surface={
                                    surfaceType === SurfaceType.SurfaceTypeSnapchat ? (
                                        <PreviewSnapchat />
                                    ) : (
                                        <PreviewSpectacles />
                                    )
                                }
                                actionType={<PreviewAdd />}
                            />
                        )}
                    </LensPreviewTextContainer>
                </LensPreviewElement>
            );
        }
        if (!src || isEmpty) {
            return <div className={grayBackground} />;
        } else if (isVideo) {
            return <VideoViewer src={src} autoplay={autoplay} />;
        } else {
            return <ImagePreview src={src} />;
        }
    }, [
        aspectRatio,
        autoplay,
        disabled,
        isEmpty,
        isReadOnly,
        isVideo,
        onKeypressShowModalTrigger,
        scale,
        showModal,
        src,
        surfaceType,
    ]);

    return (
        <PreviewViewer scale={scale} aspectRatio={aspectRatio}>
            {badgeText && (
                <BadgeAnimated className={reviewedNotification} status={badgeStatus}>
                    {badgeText}
                </BadgeAnimated>
            )}
            {renderPreviewContent()}
        </PreviewViewer>
    );
};
